import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  House, Package, Tag, Truck, ArrowsLeftRight, Warehouse, SignOut, List, X, Buildings, Users as UsersIcon, ClockCounterClockwise, UserCircle, MapPin, CloudArrowUp, Gear, Receipt, ShoppingCart, CurrencyEur, ChartBar,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { COMPANY } from "@/lib/company";

const baseNav = [
  { to: "/", label: "Dashboard", icon: House, testid: "nav-dashboard" },
  { to: "/warehouses", label: "Magazzini", icon: Buildings, testid: "nav-warehouses" },
  { to: "/inventory", label: "Giacenza", icon: Warehouse, testid: "nav-inventory" },
  { to: "/movements", label: "Movimenti", icon: ArrowsLeftRight, testid: "nav-movements" },
  { to: "/ddt", label: "DDT", icon: Receipt, testid: "nav-ddt" },
  { to: "/products", label: "Prodotti", icon: Package, testid: "nav-products" },
  { to: "/categories", label: "Categorie", icon: Tag, testid: "nav-categories" },
  { to: "/suppliers", label: "Fornitori", icon: Truck, testid: "nav-suppliers" },
  { to: "/customers", label: "Clienti", icon: UserCircle, testid: "nav-customers" },
  { to: "/commesse", label: "Commesse", icon: MapPin, testid: "nav-commesse" },
];

// Purchase requests: visible to admin, gestore, viewer (NOT operator)
const purchaseRequestsItem = {
  to: "/purchase-requests",
  label: "Richieste Acquisto",
  icon: ShoppingCart,
  testid: "nav-purchase-requests",
};

// Purchase price list: admin + gestore (used in bulk entry forms)
const purchasePricesItem = {
  to: "/purchase-prices",
  label: "Listino Acquisto",
  icon: CurrencyEur,
  testid: "nav-purchase-prices",
};

const purchaseSummaryItem = {
  to: "/purchase-summary",
  label: "Riepilogo Acquisti",
  icon: ChartBar,
  testid: "nav-purchase-summary",
};

// Transfer orders (ODT): received from Flux or created manually.
// Visible to admin, gestore, viewer (NOT operator).
const transferOrdersItem = {
  to: "/transfer-orders",
  label: "Ordini Trasferimento",
  icon: ArrowsLeftRight,
  testid: "nav-transfer-orders",
};

const adminNav = [
  { to: "/users", label: "Utenti", icon: UsersIcon, testid: "nav-users" },
  { to: "/audit", label: "Audit Log", icon: ClockCounterClockwise, testid: "nav-audit" },
  { to: "/settings", label: "Impostazioni", icon: Gear, testid: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Operator scope: they only need Giacenza (own van) + Movimenti (scarico a commessa)
  const isOperator = user && user.role === "operator";
  const operatorNav = baseNav.filter((n) => ["/inventory", "/movements"].includes(n.to));
  // Purchase requests: everyone except operator sees it
  // Purchase prices: only admin + gestore (price-sensitive data)
  const canSeePrices = user && (user.role === "admin" || user.role === "gestore");
  const extraItems = [transferOrdersItem, purchaseRequestsItem];
  if (canSeePrices) extraItems.push(purchasePricesItem, purchaseSummaryItem);
  const displayNav = isOperator ? operatorNav : [...baseNav, ...extraItems];

  const SidebarContent = ({ onNavClick }) => (
    <>
      <div className="h-16 md:h-20 flex items-center justify-between px-4 border-b border-neutral-200">
        <img src={COMPANY.logoUrl} alt="Powercom" className="h-7 md:h-8 max-w-[160px] object-contain" data-testid="sidebar-logo" />
        {onNavClick && (
          <button onClick={onNavClick} className="p-1.5 hover:bg-neutral-100 transition-colors md:hidden">
            <X size={18} />
          </button>
        )}
      </div>

      <nav className="flex-1 py-3 overflow-y-auto">
        {displayNav.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 mx-2 transition-colors duration-150 border-l-2 ${
                  isActive
                    ? "bg-neutral-100 border-[#002FA7] text-neutral-900 font-medium"
                    : "border-transparent text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
                }`
              }
            >
              <Icon size={20} weight="regular" />
              <span className="text-sm">{item.label}</span>
            </NavLink>
          );
        })}
        {user?.role === "admin" && (
          <>
            <div className="my-2 mx-4 border-t border-neutral-100" />
            <div className="label-eyebrow mx-4 mb-1 mt-3">Amministrazione</div>
            {adminNav.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 mx-2 transition-colors duration-150 border-l-2 ${
                      isActive
                        ? "bg-neutral-100 border-[#002FA7] text-neutral-900 font-medium"
                        : "border-transparent text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
                    }`
                  }
                >
                  <Icon size={20} weight="regular" />
                  <span className="text-sm">{item.label}</span>
                </NavLink>
              );
            })}
          </>
        )}
      </nav>

      <div className="border-t border-neutral-200 p-3">
        <div className="mb-3 px-1">
          <div className="text-xs text-neutral-500 truncate">{user?.email}</div>
          <div className="label-eyebrow mt-1">{user?.role}</div>
        </div>
        <div className="mb-3 px-1 pt-3 border-t border-neutral-100">
          <div className="text-[10px] uppercase tracking-wider text-neutral-400 leading-relaxed">
            {COMPANY.name}<br />
            {COMPANY.vat}
          </div>
        </div>
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors duration-150"
        >
          <SignOut size={18} />
          <span>Esci</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-neutral-50 text-neutral-900">
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-neutral-200 flex items-center justify-between px-4 z-30">
        <img src={COMPANY.logoUrl} alt="Powercom" className="h-6" />
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setMobileOpen(true)}
          className="p-2 hover:bg-neutral-100 transition-colors"
        >
          <List size={20} />
        </button>
      </div>

      {/* Desktop sidebar */}
      <aside data-testid="app-sidebar" className="hidden md:flex w-64 shrink-0 bg-white border-r border-neutral-200 flex-col">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="md:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setMobileOpen(false)} />
          <aside className="md:hidden fixed top-0 left-0 bottom-0 w-72 bg-white border-r border-neutral-200 flex flex-col z-50">
            <SidebarContent onNavClick={() => setMobileOpen(false)} />
          </aside>
        </>
      )}

      {/* Main content */}
      <main className="flex-1 min-w-0 pt-14 md:pt-0 overflow-x-hidden">{children}</main>
    </div>
  );
}
